/**
 * Lazy wrapper around pdfjs-dist.
 *
 * pdfjs-dist is ~600 KB minified — we import it dynamically inside async
 * functions so the initial bundle stays small.
 */

let pdfjsPromise: Promise<typeof import('pdfjs-dist')> | null = null;

async function getPdfjs(): Promise<typeof import('pdfjs-dist')> {
  if (!pdfjsPromise) {
    pdfjsPromise = (async () => {
      const pdfjs = await import('pdfjs-dist');
      // The worker is loaded as a module worker. Vite resolves the `?url` import
      // at build time to a hashed asset; in dev it serves the source file.
      const workerUrl = (await import('pdfjs-dist/build/pdf.worker.min.mjs?url')).default;
      pdfjs.GlobalWorkerOptions.workerSrc = workerUrl;
      return pdfjs;
    })();
  }
  return pdfjsPromise;
}

export interface OpenedPdf {
  pageCount: number;
  /** Extract concatenated text content from a page range (1-indexed, inclusive). */
  extractText: (start: number, end: number) => Promise<string>;
  /** Render a page as a data-URL PNG thumbnail at the given max edge. */
  renderThumbnail: (pageNum: number, maxEdge?: number) => Promise<string>;
  /** Free the document handle. Always call when done. */
  destroy: () => Promise<void>;
}

export async function openPdf(source: File | ArrayBuffer): Promise<OpenedPdf> {
  const pdfjs = await getPdfjs();
  const data = source instanceof File ? await source.arrayBuffer() : source;
  const loadingTask = pdfjs.getDocument({ data });
  const doc = await loadingTask.promise;

  const pageCount = doc.numPages;

  return {
    pageCount,

    extractText: async (start, end) => {
      const lo = Math.max(1, Math.min(pageCount, Math.floor(start)));
      const hi = Math.max(lo, Math.min(pageCount, Math.floor(end)));
      const chunks: string[] = [];
      for (let p = lo; p <= hi; p++) {
        const page = await doc.getPage(p);
        const content = await page.getTextContent();
        const text = content.items
          .map((it) => ('str' in it ? it.str : ''))
          .filter(Boolean)
          .join(' ');
        chunks.push(`--- p.${p} ---\n${text}`);
        page.cleanup();
      }
      return chunks.join('\n\n');
    },

    renderThumbnail: async (pageNum, maxEdge = 240) => {
      const page = await doc.getPage(pageNum);
      const viewport = page.getViewport({ scale: 1 });
      const scale = maxEdge / Math.max(viewport.width, viewport.height);
      const scaled = page.getViewport({ scale });
      const canvas = document.createElement('canvas');
      canvas.width = Math.ceil(scaled.width);
      canvas.height = Math.ceil(scaled.height);
      const ctx = canvas.getContext('2d');
      if (!ctx) throw new Error('Could not acquire 2D context for thumbnail');
      await page.render({ canvasContext: ctx, viewport: scaled }).promise;
      const url = canvas.toDataURL('image/png');
      page.cleanup();
      return url;
    },

    destroy: async () => {
      await doc.destroy();
    },
  };
}
